import { useQuery } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useFilters } from "@/lib/filters";
import { fmtHours, fmtRelative } from "@/lib/format";
import { num, rows, type NameValue } from "@/lib/kpi";
import {
  ErrorState,
  GapList,
  KpiCard,
  Leaderboard,
  LoadingState,
  PageHeader,
  Panel,
  StatusPill,
} from "@/components/dash/ui";

export default function SupportPage() {
  const { range } = useFilters();
  const q = useQuery({ queryKey: ["src", "chat", range], queryFn: () => dashApi.source("chat", range) });
  const k = q.data?.kpis;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Support / Chat"
        description="IronChat conversations, tickets and SLA performance."
        right={q.data && <span className="flex items-center gap-2"><StatusPill status={q.data.status} /><span className="text-xs text-slate-500">{fmtRelative(q.data.captured_at)}</span></span>}
      />
      {q.isLoading ? <LoadingState /> : q.isError ? <ErrorState error={q.error} /> : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <KpiCard label="New convos" value={num(k, "conversations_new")} unit="count" delta={{ pct: pctDelta(num(k, "conversations_new"), num(k, "prev_conversations_new")) }} />
            <KpiCard label="Open convos" value={num(k, "conversations_open")} unit="count" />
            <KpiCard label="Closed convos" value={num(k, "conversations_closed")} unit="count" />
            <KpiCard label="New tickets" value={num(k, "tickets_new")} unit="count" />
            <KpiCard label="Open tickets" value={num(k, "tickets_open")} unit="count" />
            <KpiCard label="SLA breaches" value={num(k, "sla_breaches")} unit="count" tone="danger" />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Panel title="First response (median)">
              <p className="text-3xl font-semibold text-white">{fmtHours(num(k, "first_response_p50_hours"))}</p>
              <p className="mt-1 text-xs text-slate-500">avg {fmtHours(num(k, "first_response_avg_hours"))}</p>
            </Panel>
            <Panel title="Resolution (median)">
              <p className="text-3xl font-semibold text-white">{fmtHours(num(k, "resolution_p50_hours"))}</p>
              <p className="mt-1 text-xs text-slate-500">avg {fmtHours(num(k, "resolution_avg_hours"))}</p>
            </Panel>
            <Panel title="Currently breached">
              <p className="text-3xl font-semibold text-rose-300">{num(k, "sla_breached_now") ?? 0}</p>
              <p className="mt-1 text-xs text-slate-500">open conversations past SLA</p>
            </Panel>
          </div>
          <Panel title="Top agents (by replies)">
            <Leaderboard rows={rows<NameValue>(k, "top_agents")} unit="count" />
          </Panel>
          {q.data && <GapList gaps={q.data.gaps} />}
        </>
      )}
    </div>
  );
}

function pctDelta(cur: number | null, prev: number | null): number | null {
  if (cur === null || prev === null || prev === 0) return null;
  return ((cur - prev) / prev) * 100;
}
